// 版本快照面板（任务 3.5）· 保存快照 / 回滚（服务端自动先快照当前态）/ 直出该版本 pptx

import { useEffect, useState } from "react";
import { api, type DeckVersion } from "../../lib/api";

interface VersionsPanelProps {
  deckId: string;
  refreshKey: number;
  onSnapshot: () => Promise<void>;
  onRestore: (versionId: string, version: number) => Promise<void>;
  onExportVersion: (versionId: string) => Promise<void>;
  onToast: (msg: string) => void;
}

export default function VersionsPanel({ deckId, refreshKey, onSnapshot, onRestore, onExportVersion, onToast }: VersionsPanelProps) {
  const [versions, setVersions] = useState<DeckVersion[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api.listVersions(deckId).then((r) => setVersions(r.versions)).catch(() => setVersions([]));
  }, [deckId, refreshKey]);

  async function run(key: string, fn: () => Promise<void>, okMsg?: string) {
    setBusy(key);
    try {
      await fn();
      if (okMsg) onToast(okMsg);
    } catch (e) {
      onToast(`操作失败：${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card !p-4">
      <h4 className="mb-2 text-sm font-bold text-ink">📷 版本快照</h4>
      <button
        className="btn-ghost w-full !py-1.5 text-xs"
        disabled={busy !== null}
        onClick={() => void run("snap", onSnapshot)}
      >
        {busy === "snap" ? "保存中…" : "保存当前为版本快照"}
      </button>
      <div className="mt-3 space-y-2">
        {versions === null && <p className="text-xs text-muted">加载中…</p>}
        {versions?.length === 0 && (
          <p className="text-xs text-muted">还没有快照。编辑一段后点上方按钮留档，回滚前系统也会自动快照。</p>
        )}
        {versions?.map((v) => (
          <div key={v.id} className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs ring-1 ring-slate-200">
            <div className="font-medium text-ink">
              v{v.version}
              <span className="ml-1.5 font-normal text-muted">{new Date(v.created_at).toLocaleString("zh-CN")}</span>
            </div>
            {v.note && <div className="mt-0.5 line-clamp-1 text-muted" title={v.note}>{v.note}</div>}
            <div className="mt-1.5 flex gap-1.5">
              <button
                className="btn-ghost !px-2 !py-0.5 text-[11px]"
                disabled={busy !== null}
                onClick={() => void run(v.id, () => onRestore(v.id, v.version))}
              >
                {busy === v.id ? "处理中…" : "回滚"}
              </button>
              <button
                className="btn-ghost !px-2 !py-0.5 text-[11px]"
                disabled={busy !== null}
                onClick={() => void run(`exp-${v.id}`, () => onExportVersion(v.id))}
              >
                {busy === `exp-${v.id}` ? "渲染中…" : "导出此版本"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
